import { Component, effect, input, output, signal } from '@angular/core'
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { SelectInputComponent } from '@app/v2/shared/components/inputs/select-input/select-input.component'
import { IndustrySector } from '@app/v2/shared/types/industry-sector.type'
import { LegalInfoIcons } from '../../models/organization-settings.model'
import { TranslateModule } from '@ngx-translate/core'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { OrganizationSettings } from '../../types/organization-settings.type'
import { OrganizationService } from '../../services/organization.service'
import { UploadComponent } from '@app/v2/shared/components/upload/upload.component'
import { UploadTypeEnum } from '@app/v2/auth/types/media.type'

@Component({
  selector: 'app-legal-info',
  templateUrl: './legal-info.component.html',
  styleUrls: ['./legal-info.component.scss'],
  standalone: true,
  imports: [BasicInputComponent, SelectInputComponent, ReactiveFormsModule, UploadComponent, TranslateModule, NgxSkeletonLoaderModule]
})
export class LegalInfoComponent {
  public industrySectors = input<IndustrySector[]>([])
  public organizationData = input<OrganizationSettings | null>(null)
  public isLoading = input<boolean>(true)
  public updateRequested = output<void>()

  public form = signal<FormGroup | null>(null)
  public companyName = signal<FormControl>(new FormControl('', [Validators.maxLength(200)]))
  public licenseNumber = signal<FormControl>(new FormControl('', [Validators.maxLength(200)]))
  public taxIdentificationNumber = signal<FormControl>(new FormControl('', [Validators.maxLength(200)]))
  public industrySectorId = signal<FormControl>(new FormControl(''))
  public licensesPermits = new FormArray([])

  public icons = LegalInfoIcons.getIconsConfiguration()
  // Upload options for legal documents
  public uploadTypeEnum = UploadTypeEnum

  constructor(private _organizationService: OrganizationService) {
    this._initLegalInfoForm()
    effect(() => {
      if (!this.isLoading() && this.organizationData()) this._getLegalInfo(this.organizationData())
    })
  }

  /**
   * Initialize the form
   */
  private _initLegalInfoForm() {
    this.form.set(
      new FormGroup({
        companyName: this.companyName(),
        licenseNumber: this.licenseNumber(),
        taxIdentificationNumber: this.taxIdentificationNumber(),
        industrySectorId: this.industrySectorId(),
        licensesPermits: this.licensesPermits
      })
    )
  }

  /**
   * Get the legal info
   * @param {OrganizationSettings} data Organization settings
   */
  private _getLegalInfo(data: OrganizationSettings): void {
    this.companyName().setValue(data.company_name)
    this.licenseNumber().setValue(data.license_number)
    this.taxIdentificationNumber().setValue(data.tax_identification_number)
    this.industrySectorId().setValue(this.industrySectors().find((sector) => sector.id === data.industry_sector_id)?.id)
    if (!this.licensesPermits?.value.length)
      data?.licenses_permits?.forEach((permit) => {
        this.licensesPermits.push(
          new FormGroup({
            path: new FormControl(permit.path),
            name: new FormControl(permit.name),
            id: new FormControl(permit.id)
          })
        )
      })
  }

  /**
   * On enter key
   * @param {Event} event Event
   */
  public onEnterKey(event: Event): void {
    event.preventDefault()
    if (this.isFormDirty()) this.updateRequested.emit()
  }

  /**
   * On blur event
   * @param {FocusEvent} event Event
   */
  public onBlur(event: FocusEvent): void {
    if (this.isFormDirty()) this.updateRequested.emit()
  }

  /**
   * On child keydown
   * @param {KeyboardEvent} event Event
   */
  public onChildKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      if (this.isFormDirty()) this.updateRequested.emit()
    }
  }

  /**
   * On business activity changed
   * @param {number} id Industry sector id
   */
  public onBusinessActivityChanged(id: number): void {
    this.industrySectorId().setValue(id)
    this.updateRequested.emit()
  }

  /**
   * Check if form has unsaved changes
   */
  public isFormDirty(): boolean {
    return this._organizationService.isFormDirty(this.form()!)
  }

  /**
   * @description Gets skeleton theme colors based on current theme
   * @returns {object} Skeleton theme colors
   */
  public getSkeletonTheme(): { backgroundColor: string; color: string } {
    return this._organizationService.getSkeletonTheme()
  }

  /**
   * On permits license changed
   */
  public onPermitsLicenseChanged(): void {
    this.updateRequested.emit()
  }
}
